/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import { ICalendarDateRange } from '@models';
import {
  CreateUnavailability,
  PagedUnavailability,
  UnavailabilityService,
  Unavailability,
} from '@services';
import { message } from 'antd';
import { makeAutoObservable, runInAction } from 'mobx';

class UnavailabilityStore {
  unavailability = new Map<string, Unavailability>();
  calendarUnavailability = new Map<string, Unavailability>();
  totalCount: number = 0;
  skipCount: number = 0;
  maxResultCount: number = 15;
  loading: boolean = false;
  currentPage: number = 1;

  constructor() {
    makeAutoObservable(this);
  }

  getAll = async (params: PagedUnavailability) => {
    this.setLoading(true);
    try {
      const result = await UnavailabilityService.getAll(params);
      runInAction(() => {
        this.unavailability.clear();
        result.items.forEach((item) => {
          this.unavailability.set(item.id, item);
        });
        this.totalCount = result.totalCount;
      });
    } catch (error) {
      console.error('Error fetching unavailability:', error);
    } finally {
      this.setLoading(false);
    }
  };

  getAllCalendar = async (date: ICalendarDateRange | undefined) => {
    this.setLoading(true);
    try {
      const result = await UnavailabilityService.getAllCalendar(date);
      runInAction(() => {
        result.forEach((item) => {
          this.calendarUnavailability.set(item.id, item);
        });
      });
    } catch (error) {
      console.error('Error fetching unavailability:', error);
    } finally {
      this.setLoading(false);
    }
  };

  create = async (input: CreateUnavailability): Promise<Unavailability | undefined> => {
    this.setLoading(true);
    try {
      const result = await UnavailabilityService.create(input);
      runInAction(() => {
        this.calendarUnavailability.set(result.id, result);
        this.unavailability.set(result.id, result);
        this.totalCount += 1;
      });
      message.success({ content: 'Unavailability added' });
      return result;
    } catch (error) {
      console.error('Error creating unavailability:', error);
      message.error({ content: 'Could not add the unavailability, please try again.' });
      return undefined;
    } finally {
      this.setLoading(false);
    }
  };

  delete = async (id: string) => {
    this.setLoading(true);
    try {
      await UnavailabilityService.delete(id);
      runInAction(() => {
        this.calendarUnavailability.delete(id);
        this.unavailability.delete(id);
        this.totalCount -= 1;
      });
    } catch (error) {
      console.error('Error deleting unavailability:', error);
    } finally {
      this.setLoading(false);
    }
  };

  setLoading = (value: boolean) => {
    runInAction(() => {
      this.loading = value;
    });
  };

  setSkipCount = (value: number) => {
    runInAction(() => {
      this.skipCount = value;
    });
  };

  setCurrentPage = (value: number) => {
    runInAction(() => {
      this.currentPage = value;
    });
  };

  handlePageChange = (pagination: any) => {
    runInAction(() => {
      this.maxResultCount = pagination.pageSize;
      this.setSkipCount((pagination.current - 1) * pagination.pageSize);
      this.setCurrentPage(pagination.current);
    });
  };

  clearUnavailability = () => {
    runInAction(() => {
      this.unavailability.clear();
    });
  };

  resetUnavailability = () => {
    runInAction(() => {
      this.unavailability.clear();
      this.totalCount = 0;
      this.skipCount = 0;
    });
  };

  clearCalendarUnavailability = () => {
    runInAction(() => {
      this.calendarUnavailability.clear();
    });
  };
}

export default UnavailabilityStore;
